import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { G } from "@/brand";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { X, Mail } from "lucide-react";

export function AuthModal() {
  const { authOpen, setAuthOpen, authReason } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState("");
  const [sent, setSent] = useState(false);

  if (!authOpen) return null;

  const send = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/magic-link", { email, origin: window.location.origin });
      if (data.magic_link) setLink(data.magic_link);
      else setSent(true);
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const close = () => { setAuthOpen(false); setEmail(""); setLink(""); setSent(false); setError(""); };

  const goVerify = () => {
    const url = new URL(link);
    close();
    nav(url.pathname + url.search);
  };

  return (
    <div data-testid="auth-modal" onClick={close} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...box, position: "relative" }}>
        <button onClick={close} data-testid="auth-close" style={closeBtn}>×</button>
        <div className="mono" style={{ fontSize: 9, letterSpacing: "0.5em", color: G.gold, marginBottom: 16 }}>
          EMAIL MAGIC LINK
        </div>

        {sent ? (
          <>
            <div style={{ fontSize: 24, fontWeight: 300, fontStyle: "italic", color: "#fff", marginBottom: 12 }}>
              ✦ Check your email
            </div>
            <div style={{ color: G.muted, fontSize: 14, fontStyle: "italic", marginBottom: 4, lineHeight: 1.6 }}>
              We sent a one-tap sign-in link to <b style={{ color: G.gold }}>{email}</b>. It expires in 30 minutes.
            </div>
          </>
        ) : !link ? (
          <>
            <div style={{ fontSize: 26, fontWeight: 300, fontStyle: "italic", color: "#fff", marginBottom: 8 }}>
              Sign in to continue
            </div>
            <div style={{ color: G.muted, fontSize: 14, fontStyle: "italic", marginBottom: 24, lineHeight: 1.6 }}>
              {authReason || "Enter your email and we'll send you a one-tap sign-in link. No password needed."}
            </div>
            <div style={{ position: "relative", marginBottom: 8 }}>
              <Mail size={15} style={{ position: "absolute", left: 14, top: 14, color: G.goldDim }} />
              <input
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && email && send()}
                placeholder="you@email.com"
                style={{ width: "100%", padding: "12px 16px 12px 40px", background: G.panel, border: `1px solid ${G.border}`, color: G.text, fontSize: 14 }}
              />
            </div>
            {error && <div className="mono" style={{ color: "#ff5555", fontSize: 11, marginBottom: 12, letterSpacing: ".05em" }}>{error}</div>}
            <button data-testid="auth-send-btn" onClick={send} disabled={loading || !email} className="btn-gold" style={{ width: "100%", padding: "13px", fontSize: 10, marginTop: 8 }}>
              {loading ? "Sending…" : "Send Magic Link"}
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 24, fontWeight: 300, fontStyle: "italic", color: "#fff", marginBottom: 12 }}>
              ✦ Link ready
            </div>
            <div style={{ color: G.muted, fontSize: 13, fontStyle: "italic", marginBottom: 18, lineHeight: 1.6 }}>
              In production this arrives by email (Resend). For now, tap below to sign in instantly.
            </div>
            <button data-testid="auth-open-link-btn" onClick={goVerify} className="btn-gold" style={{ width: "100%", padding: "13px", fontSize: 10 }}>
              Open Magic Link & Sign In
            </button>
            <div className="mono" style={{ color: G.goldDim, fontSize: 9, marginTop: 14, wordBreak: "break-all", lineHeight: 1.5 }}>
              {link}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const box = { background: G.dark, border: `1px solid ${G.borderHot}`, padding: "44px 38px", maxWidth: 440, width: "100%" };
const closeBtn = { position: "absolute", top: 14, right: 20, background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 24, lineHeight: 1 };
